// Design tokens - single source of truth
// These are injected into SCSS via sass-loader and can be imported in JS

const baseBreakpoints = {
  xs: '375px',
  sm: '480px',
  smTablet: '600px',
  md: '768px',
  ipad: '767px',
  ipadHeight: '1024px',
  lg: '1024px',
  tabletMax: '1023px',
  xl: '1280px',
  xlWide: '1366px',
  xxl: '1440px',
  xxxl: '1920px',
};

const tokens = {
  colors: {
    // Primary
    purple100: '#f1ecff',
    purple300: '#cdc0f3',
    purple400: '#9c7edb',
    purple500: '#7e3dd4',
    purple700: '#52258f',
    purple900: '#1e0b3a',

    // Accent
    accent100: '#f7c7ee',
    accent500: '#e55ccd',
    accent900: '#a92c93',

    // Highlight
    highlight500: '#e0ff00',

    // Secondary (Deprecated as of 2024 Rebrand)
    teal100: '#e4f6f6',
    teal200: '#abddda',
    teal300: '#11ada2',
    teal500: '#037a72',

    // Mono
    mono0: '#fff',
    mono100: '#f2f2ec',
    mono300: '#e6e4d6',
    mono400: '#c0bda2',
    mono500: '#9e9c88',
    mono700: '#605f56',
    mono900: '#1e0b3a',

    // Success
    green100: '#e5f4e7',
    green300: '#028810',
    green500: '#155e34',

    // Info
    blue100: '#e9f7ff',
    blue300: '#0177b0',
    blue500: '#005781',

    // Warning
    orange100: '#fdf3e6',
    orange300: '#eb7f00',
    orange500: '#c0490a',

    // Alert
    red100: '#fff0ee',
    red300: '#d72505',
    red500: '#a32100',
    red900: '#5d1300',

    // Legacy aliases for backward compatibility
    pinkPale: '#f1ecff',
    lilac: '#f1ecff',
    primary050: '#f1ecff',
    violetLight: '#cdc0f3',
    purplePinkLight: '#cdc0f3',
    fiolet: '#cdc0f3',
    purpleLight: '#cdc0f3',
    primaryA3: '#7e3dd4',
    pink: '#7e3dd4',
    purplePinkMedium: '#7e3dd4',
    primaryA1: '#7e3dd4',
    primaryA2: '#52258f',
    purpleDark: '#52258f',
    violetDark: '#52258f',
    purple: '#52258f',
    purpleXdark: '#1e0b3a',
    aqua: '#11ada2',
    irisBlue: '#11ada2',
    blueDark: '#037a72',
    white: '#fff',
    ghostWhite: '#fff',
    blueDuckEgg: '#f2f2ec',
    grayLight: '#f2f2ec',
    gray94: '#f2f2ec',
    concreteGrey: '#f2f2ec',
    grayMediumLight: '#e6e4d6',
    santorini: '#e6e4d6',
    grayMedium: '#e6e4d6',
    monoGray: '#9e9c88',
    neutral300: '#9e9c88',
    gray77: '#9e9c88',
    neutral500: '#605f56',
    grayXdark: '#605f56',
    monoDark: '#605f56',
    grayDark: '#605f56',
    sealGrey: '#605f56',
    brightGrey: '#605f56',
    neutral600: '#605f56',
    neutral700: '#605f56',
    navyLight: '#605f56',
    darkGray: '#605f56',
    eerieBlack: '#1e0b3a',
    oxfordBlue: '#1e0b3a',
    navy: '#1e0b3a',
    primaryText: '#1e0b3a',
    greenLightest: '#e5f4e7',
    greenLight: '#028810',
    green: '#028810',
    lime: '#028810',
    greenDark: '#155e34',
    frostedMint: '#e9f7ff',
    blueLightest: '#e9f7ff',
    secondaryA2: '#e9f7ff',
    blue: '#0177b0',
    bluePaymentsHub: '#0177b0',
    b500: '#0177b0',
    blueLight: '#0177b0',
    blueMedium: '#0177b0',
    blueSky: '#0177b0',
    blue2: '#005781',
    blueSteel: '#005781',
    blueRoyal: '#005781',
    blueFacebook: '#005781',
    yellowMediumDark: '#fdf3e6',
    yellowLight: '#fdf3e6',
    yellowMedium: '#fdf3e6',
    shift01: '#fdf3e6',
    yellow: '#fdf3e6',
    yellowDark: '#eb7f00',
    shift02: '#eb7f00',
    orangeYellow: '#eb7f00',
    orange: '#eb7f00',
    orangeDark: '#c0490a',
    orangePayAnywhere: '#c0490a',
    alertA1: '#fff0ee',
    orangeLight: '#fff0ee',
    pinkLight: '#fff0ee',
    orangeIndeed: '#d72505',
    orangeIndeedDark: '#d72505',
    red: '#d72505',
    redOrange: '#d72505',
    red3: '#a32100',
    redWarning: '#a32100',
    red2: '#a32100',
    borderColor: '#e6e4d6',
    errorColor: '#d72505',
    whiteMedium: '#f2f2ec',
    blackLight: '#1e0b3a',
    black: '#1e0b3a',
    grayLightest: '#f2f2ec',
    grayMediumLighter: '#e6e4d6',
    grayMediumDark: '#605f56',
    redMedium: '#d72505',
    navyDark: '#1e0b3a',
    navyMedium: '#1e0b3a',
    purpleLighter: '#cdc0f3',
    purpleMedium: '#52258f',
    violet: '#cdc0f3',
    violetMedium: '#cdc0f3',
    violetXdark: '#52258f',
  },

  // Legacy color aliases (used by theme/constants.js)
  legacyColors: {
    // FDB colors
    purple100: '#f1ecff',
    purple300: '#cdc0f3',
    purple400: '#9c7edb',
    purple500: '#7e3dd4',
    purple700: '#52258f',
    purple900: '#1e0b3a',
    teal100: '#e4f6f6',
    teal200: '#abddda',
    teal300: '#11ada2',
    teal500: '#037a72',
    mono0: '#fff',
    mono100: '#f2f2ec',
    mono300: '#e6e4d6',
    mono400: '#c0bda2',
    mono500: '#9e9c88',
    mono700: '#605f56',
    mono900: '#1e0b3a',
    green100: '#e5f4e7',
    green300: '#028810',
    green500: '#155e34',
    blue100: '#e9f7ff',
    blue300: '#0177b0',
    blue500: '#005781',
    orange100: '#fdf3e6',
    orange300: '#eb7f00',
    orange500: '#c0490a',
    red100: '#fff0ee',
    red300: '#d72505',
    red500: '#a32100',
    red900: '#5d1300',
    accent100: '#f7c7ee',
    accent500: '#e55ccd',
    accent900: '#a92c93',
    highlight500: '#e0ff00',

    // Legacy aliases for backward compatibility
    pinkPale: '#f1ecff',
    lilac: '#f1ecff',
    primary050: '#f1ecff',
    violetLight: '#cdc0f3',
    purplePinkLight: '#cdc0f3',
    fiolet: '#cdc0f3',
    purpleLight: '#cdc0f3',
    primaryA3: '#7e3dd4',
    pink: '#7e3dd4',
    purplePinkMedium: '#7e3dd4',
    primaryA1: '#7e3dd4',
    primaryA2: '#52258f',
    purpleDark: '#52258f',
    violetDark: '#52258f',
    purple: '#52258f',
    purpleXdark: '#1e0b3a',
    aqua: '#11ada2',
    irisBlue: '#11ada2',
    blueDark: '#037a72',
    white: '#fff',
    ghostWhite: '#fff',
    blueDuckEgg: '#f2f2ec',
    grayLight: '#f2f2ec',
    gray94: '#f2f2ec',
    concreteGrey: '#f2f2ec',
    grayMediumLight: '#e6e4d6',
    santorini: '#e6e4d6',
    grayMedium: '#e6e4d6',
    monoGray: '#9e9c88',
    neutral300: '#9e9c88',
    gray77: '#9e9c88',
    neutral500: '#605f56',
    grayXdark: '#605f56',
    monoDark: '#605f56',
    grayDark: '#605f56',
    sealGrey: '#605f56',
    brightGrey: '#605f56',
    neutral600: '#605f56',
    neutral700: '#605f56',
    navyLight: '#605f56',
    darkGray: '#605f56',
    eerieBlack: '#1e0b3a',
    oxfordBlue: '#1e0b3a',
    navy: '#1e0b3a',
    primaryText: '#1e0b3a',
    greenLightest: '#e5f4e7',
    greenLight: '#028810',
    green: '#028810',
    lime: '#028810',
    greenDark: '#155e34',
    frostedMint: '#e9f7ff',
    blueLightest: '#e9f7ff',
    secondaryA2: '#e9f7ff',
    blue: '#0177b0',
    bluePaymentsHub: '#0177b0',
    b500: '#0177b0',
    blueLight: '#0177b0',
    blueMedium: '#0177b0',
    blueSky: '#0177b0',
    blue2: '#005781',
    blueSteel: '#005781',
    blueRoyal: '#005781',
    blueFacebook: '#005781',
    yellowMediumDark: '#fdf3e6',
    yellowLight: '#fdf3e6',
    yellowMedium: '#fdf3e6',
    shift01: '#fdf3e6',
    yellow: '#fdf3e6',
    yellowDark: '#eb7f00',
    shift02: '#eb7f00',
    orangeYellow: '#eb7f00',
    orange: '#eb7f00',
    orangeDark: '#c0490a',
    orangePayAnywhere: '#c0490a',
    alertA1: '#fff0ee',
    orangeLight: '#fff0ee',
    pinkLight: '#fff0ee',
    orangeIndeed: '#d72505',
    orangeIndeedDark: '#d72505',
    red: '#d72505',
    redOrange: '#d72505',
    red3: '#a32100',
    redWarning: '#a32100',
    red2: '#a32100',
    borderColor: '#e6e4d6',
    errorColor: '#d72505',
    whiteMedium: '#f2f2ec',
    blackLight: '#1e0b3a',
    black: '#1e0b3a',
    grayLightest: '#f2f2ec',
    grayMediumLighter: '#e6e4d6',
    grayMediumDark: '#605f56',
    redMedium: '#d72505',
    navyDark: '#1e0b3a',
    navyMedium: '#1e0b3a',
    purpleLighter: '#cdc0f3',
    purpleMedium: '#52258f',
    violet: '#cdc0f3',
    violetMedium: '#cdc0f3',
    violetXdark: '#52258f',
  },

  cloverColors: {
    // Clover theme colors (green-based)
    purple100: '#f2fcee',
    purple300: '#bee4b2',
    purple400: '#8bd174',
    purple500: '#280',
    purple700: '#276a16',
    purple900: '#000',

    accent100: '#f2fcee',
    accent500: '#280',
    accent900: '#276a16',

    highlight500: '#280',

    teal100: '#e4f6f6',
    teal200: '#abddda',
    teal300: '#11ada2',
    teal500: '#037a72',

    mono0: '#fff',
    mono100: '#f7f9fb',
    mono300: '#e5e5e5',
    mono400: '#c7c7c7',
    mono500: '#c7c7c7',
    mono700: '#5c5c5c',
    mono900: '#000',

    green100: '#f2fcee',
    green300: '#bee4b2',
    green500: '#280',

    blue100: '#eefbfc',
    blue300: '#69d0dd',
    blue500: '#286271',

    orange100: '#fff6cc',
    orange300: '#eb7f00',
    orange500: '#c0490a',

    red100: '#ffeded',
    red300: '#c60000',
    red500: '#a32100',
    red900: '#5d1300',
  },

  legacyCloverColors: {
    // Legacy variable names mapped to Clover colors (for backwards compatibility)
    // FDB purple100
    pinkPale: '#f2fcee',
    lilac: '#f2fcee',
    primary050: '#f2fcee',

    // FDB purple300
    violetLight: '#bee4b2',
    purplePinkLight: '#bee4b2',
    fiolet: '#bee4b2',
    purpleLight: '#bee4b2',

    // FDB purple400
    purple400: '#8bd174',

    // FDB purple500
    primaryA3: '#280',
    pink: '#280',
    purplePinkMedium: '#280',
    primaryA1: '#280',

    // FDB purple700
    primaryA2: '#276a16',
    purpleDark: '#276a16',
    violetDark: '#276a16',
    purple: '#276a16',

    // FDB purple900
    purpleXdark: '#000',

    // FDB teal300
    aqua: '#11ada2',
    irisBlue: '#11ada2',

    // FDB teal500
    blueDark: '#037a72',

    // FDB mono0
    white: '#fff',
    ghostWhite: '#fff',

    // FDB mono100
    blueDuckEgg: '#f7f9fb',
    grayLight: '#f7f9fb',
    gray94: '#f7f9fb',
    concreteGrey: '#f7f9fb',

    // FDB mono300
    grayMediumLight: '#e5e5e5',
    santorini: '#e5e5e5',
    grayMedium: '#e5e5e5',

    // FDB mono500
    monoGray: '#c7c7c7',
    neutral300: '#c7c7c7',
    gray77: '#c7c7c7',

    // FDB mono700
    neutral500: '#5c5c5c',
    grayXdark: '#5c5c5c',
    monoDark: '#5c5c5c',
    grayDark: '#5c5c5c',
    sealGrey: '#5c5c5c',
    brightGrey: '#5c5c5c',
    neutral600: '#5c5c5c',
    neutral700: '#5c5c5c',
    navyLight: '#5c5c5c',
    darkGray: '#5c5c5c',

    // FDB mono900
    eerieBlack: '#000',
    oxfordBlue: '#000',
    navy: '#000',
    primaryText: '#000',

    // FDB green100
    greenLightest: '#f2fcee',

    // FDB green300
    greenLight: '#bee4b2',
    green: '#bee4b2',
    lime: '#bee4b2',

    // FDB green500
    greenDark: '#280',

    // FDB blue100
    frostedMint: '#eefbfc',
    blueLightest: '#eefbfc',
    secondaryA2: '#eefbfc',

    // FDB blue300
    blue: '#69d0dd',
    bluePaymentsHub: '#69d0dd',
    b500: '#69d0dd',
    blueLight: '#69d0dd',
    blueMedium: '#69d0dd',
    blueSky: '#69d0dd',

    // FDB blue500
    blue2: '#286271',
    blueSteel: '#286271',
    blueRoyal: '#286271',
    blueFacebook: '#286271',

    // FDB orange100
    yellowMediumDark: '#fff6cc',
    yellowLight: '#fff6cc',
    yellowMedium: '#fff6cc',
    shift01: '#fff6cc',
    yellow: '#fff6cc',

    // FDB orange300
    yellowDark: '#eb7f00',
    shift02: '#eb7f00',
    orangeYellow: '#eb7f00',
    orange: '#eb7f00',

    // FDB orange500
    orangeDark: '#c0490a',
    orangePayAnywhere: '#c0490a',

    // FDB red100
    alertA1: '#ffeded',
    orangeLight: '#ffeded',
    pinkLight: '#ffeded',

    // FDB red300
    orangeIndeed: '#c60000',
    orangeIndeedDark: '#c60000',
    red: '#c60000',
    redOrange: '#c60000',

    // FDB red500
    red3: '#a32100',
    redWarning: '#a32100',
    red2: '#a32100',

    // Misc/derived
    borderColor: '#e5e5e5',
    errorColor: '#c60000',

    // Vestigial
    whiteMedium: '#f7f9fb',
    blackLight: '#000',
    black: '#000',
    grayLightest: '#f7f9fb',
    grayMediumLighter: '#e5e5e5',
    grayMediumDark: '#5c5c5c',
    redMedium: '#c60000',
    navyDark: '#000',
    navyMedium: '#000',
    purpleLighter: '#bee4b2',
    purpleMedium: '#276a16',
    violet: '#bee4b2',
    violetMedium: '#bee4b2',
    violetXdark: '#276a16',
  },

  breakpoints: {
    smallBreakpoint: baseBreakpoints.sm,
    mediumBreakpoint: baseBreakpoints.md,
    mediumLargeBreakpoint: baseBreakpoints.lg,
    largeBreakpoint: baseBreakpoints.xl,
    xlargeBreakpoint: baseBreakpoints.xxl,
    xxlargeBreakpoint: baseBreakpoints.xxxl,
  },

  zindex: {
    zindexBottomDrawer: 1000,
    zindexRainingEmoji: 1000,
    zindexMessengerViewMenu: 1001,
    zindexAvatarNav: 1002,
    zindexFdbDialog: 1300,
    zindexFdbPopper: 1300,
    zindexFdbDropdown: 1300,
    zindexFdbContextMenu: 1300,
    zindexFdbToast: 1400,
    zindexFdbTooltip: 1500,
    zindexFdbReactModal: 10000,
    zindexFlashNotice: 10010,
    zindexLeftNavContainer: 10011,
    zindexLeftNavDrawers: 14,
  },

  ai: {
    animatingSpeed: '6s',
    spinSpeed: '6s',
    position: 'absolute',
    afterInset: '-2px',
    beforeInset: '0',
    afterZIndex: -2,
    beforeZIndex: -1,
  },

  animations: {
    duration: '0.25s',
    longDuration: '0.5s',
  },

  typography: {
    cursive: 'CedarvilleCursive-Regular',
    fw300: 300,
    fw400: 400,
    fw500: 500,
    fw600: 600,
    fw700: 700,
    fw800: 800,
    fs10: '10px',
    fs12: '12px',
    fs14: '14px',
    fs16: '16px',
    fs18: '18px',
    fs24: '24px',
    fs30: '30px',
    fs32: '32px',
    fs36: '36px',
    fs40: '40px',
    fs50: '50px',
    baseFontFamily: '"Plus Jakarta Sans", sans-serif',
    baseFontWeight: 500,
    baseFontSize: '12px',
    baseLineHeight: 1.4,
    fixedFontFamily: 'monospace',
    fixedFontSize: '85%',
    fixedLineHeight: 1.4,
    headerFontWeight: 700,
    header: 700,
  },

  dimensions: {
    none: 0,
    tiny: '1px',
    xxxxs: '2px',
    xxxs: '3px',
    xxs: '7px',
    xs: '10px',
    sm: '15px',
    md: '20px',
    lg: '25px',
    xl: '30px',
    xxl: '40px',
    xxxl: '60px',
    borderWidth: '1px',
    borderRadius: '3px',
    defaultFormWidth: '500px',
    defaultFooterHeight: '60px',
    checkboxOffset: '37px',
    messengerWidth: '450px',
    messengerHeaderHeight: '47px',
    messengerControlInputWidth: '200px',
    employeeFormViewPadding: '10px 40px',
    defaultMaxWidth: '1100px',
    navHeight: '48px',
    navHeightEmployee: '88px',
    topbannerNavHeight: '75px',
    messengerNavHeight: '58px',
    messageDrawerOffset: '106px',
    userReferralsBoxContentWidth: '260px',
    userReferralsBoxContentHeight: '278px',
    sidebarWidth: '48px',
    s0: 0,
    s1: '1px',
    s2: '2px',
    s4: '4px',
    s8: '8px',
    s12: '12px',
    s16: '16px',
    s20: '20px',
    s24: '24px',
    s32: '32px',
    s36: '36px',
    s48: '48px',
    s64: '64px',
  },

  legacyBreakpoints: {
    ipadWidth: baseBreakpoints.ipad,
    ipadHeight: baseBreakpoints.ipadHeight,
    mobileBreakpoint: baseBreakpoints.sm,
    tabletBreakpoint: baseBreakpoints.tabletMax,
    smallTabletBreakpoint: baseBreakpoints.smTablet,
    largeWideDesktopBreakpoint: baseBreakpoints.xlWide,
    largeDesktopBreakpoint: baseBreakpoints.xl,
    xlargeDesktopBreakpoint: baseBreakpoints.xxl,
  },

  rails: {
    tabletHeightBuffer: '190px',
  },
};

module.exports = tokens;
